// Workflow tree — modern SaaS biotech pipeline panel.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { buildWorkflowTreeView, } from './langGraphPipelineTree';
export { PipelineBranchIcon, PipelineTreeIcon } from './langGraphPipelineIcons';
const ACCENT = '#3B5FFF';
const CHILD_ROW_H = 44;
const CHILD_GAP = 6;
function ChevronIcon({ open }) {
    return (React.createElement("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: `wf-chevron ${open ? 'wf-chevron-open' : ''}`, "aria-hidden": "true" },
        React.createElement("polyline", { points: "9 18 15 12 9 6" })));
}
function DoneIcon() {
    return (React.createElement("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true" },
        React.createElement("circle", { cx: "12", cy: "12", r: "10", stroke: "#22c55e", strokeWidth: "1.5" }),
        React.createElement("polyline", { points: "8 12 11 15 16 9", stroke: "#22c55e", strokeWidth: "1.75", strokeLinecap: "round", strokeLinejoin: "round" })));
}
function BranchConnectors({ count, highlighted, }) {
    if (count === 0)
        return null;
    const height = count * CHILD_ROW_H + (count - 1) * CHILD_GAP;
    const paths = [`M 0 0 V ${height}`];
    for (let i = 0; i < count; i += 1) {
        const y = i * (CHILD_ROW_H + CHILD_GAP) + CHILD_ROW_H / 2;
        // Curved branch — same language as pipeline-icon (arc connector)
        paths.push(`M 0 ${y} H 8 a 6 6 0 0 1 6 6 l 6 -6`);
    }
    return (React.createElement("svg", { className: `wf-connectors ${highlighted ? 'wf-connectors-highlight' : ''}`, width: "24", height: height, "aria-hidden": "true" }, paths.map((d, i) => (React.createElement("path", { key: i, d: d, fill: "none", stroke: "currentColor", strokeWidth: "1.75", strokeLinecap: "round", strokeLinejoin: "round" })))));
}
function StageBlock({ stage, expanded, onToggle, selectedStepId, onSelectStep, expandedSubtasks, onToggleSubtask, }) {
    const isMuted = stage.status === 'pending';
    const isActive = stage.status === 'active';
    const [hovered, setHovered] = useState(false);
    const visibleSubtasks = isMuted ? [] : stage.subtasks;
    const handleParentClick = () => {
        if (isMuted)
            return;
        onToggle();
    };
    return (React.createElement("div", { className: `wf-stage wf-stage-${stage.status} ${expanded ? 'wf-stage-expanded' : ''}`, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) },
        React.createElement("button", { type: "button", className: `wf-parent ${isActive ? 'wf-parent-active' : ''} ${isMuted ? 'wf-parent-muted' : ''}`, onClick: handleParentClick, "aria-expanded": expanded && !isMuted, disabled: isMuted },
            React.createElement(ChevronIcon, { open: expanded }),
            React.createElement("span", { className: "wf-parent-label" }, stage.label),
            React.createElement("span", { className: "wf-parent-meta" },
                stage.status === 'done' && React.createElement(DoneIcon, null),
                React.createElement("span", { className: "wf-progress-pct" },
                    stage.progressPct,
                    "%"))),
        React.createElement("div", { className: `wf-children-wrap ${expanded ? 'wf-children-open' : ''}` },
            React.createElement("div", { className: "wf-children-inner" }, visibleSubtasks.length > 0 && (React.createElement("div", { className: "wf-children" },
                React.createElement(BranchConnectors, { count: visibleSubtasks.length, highlighted: hovered || isActive }),
                React.createElement("div", { className: "wf-child-list" }, visibleSubtasks.map((sub) => {
                    const selected = selectedStepId === sub.stepId;
                    const subExpanded = expandedSubtasks.has(sub.id);
                    const showDetails = sub.status === 'active' ||
                        (subExpanded && sub.status === 'done');
                    return (React.createElement("div", { key: sub.id, className: "wf-child-block" },
                        React.createElement("button", { type: "button", className: `wf-child wf-child-${sub.status} ${selected ? 'wf-child-selected' : ''}`, onClick: () => {
                                onSelectStep(sub.stepId);
                                onToggleSubtask(sub.id);
                            }, "aria-current": selected ? 'step' : undefined },
                            React.createElement("span", { className: "wf-child-dot", "aria-hidden": "true" }),
                            React.createElement("span", { className: "wf-child-label" }, sub.label),
                            sub.status === 'done' && (React.createElement("span", { className: "wf-child-done", "aria-label": "Complete" },
                                React.createElement(DoneIcon, null))),
                            sub.status === 'active' && (React.createElement("span", { className: "wf-child-active-pill" }, "In progress"))),
                        showDetails && sub.details.length > 0 && (React.createElement("ul", { className: "wf-details" }, sub.details.map((d) => (React.createElement("li", { key: `${sub.id}-${d.label}`, className: `wf-detail ${d.filled ? 'wf-detail-filled' : ''}` },
                            React.createElement("span", { className: "wf-detail-label" }, d.label),
                            React.createElement("span", { className: "wf-detail-value" }, d.value))))))));
                }))))))));
}
export default function PipelineGraphView({ data, selectedStepId, onSelectStep, }) {
    const stages = useMemo(() => buildWorkflowTreeView(data === null || data === void 0 ? void 0 : data.step_progress, data === null || data === void 0 ? void 0 : data.current_step, data), [data]);
    const [expandedStages, setExpandedStages] = useState(() => new Set());
    const [expandedSubtasks, setExpandedSubtasks] = useState(() => new Set());
    useEffect(() => {
        const next = new Set();
        stages.forEach((s) => {
            if (s.defaultExpanded)
                next.add(s.id);
        });
        setExpandedStages(next);
    }, [stages]);
    useEffect(() => {
        if (!(data === null || data === void 0 ? void 0 : data.current_step))
            return;
        const activeStage = stages.find((s) => s.subtasks.some((sub) => sub.stepId === data.current_step));
        if (activeStage) {
            setExpandedStages((prev) => new Set(prev).add(activeStage.id));
            const activeSub = activeStage.subtasks.find((sub) => sub.status === 'active');
            if (activeSub) {
                setExpandedSubtasks((prev) => new Set(prev).add(activeSub.id));
            }
        }
    }, [data === null || data === void 0 ? void 0 : data.current_step, stages]);
    const toggleStage = useCallback((stageId) => {
        setExpandedStages((prev) => {
            const next = new Set(prev);
            if (next.has(stageId))
                next.delete(stageId);
            else
                next.add(stageId);
            return next;
        });
    }, []);
    const toggleSubtask = useCallback((subId) => {
        setExpandedSubtasks((prev) => {
            const next = new Set(prev);
            if (next.has(subId))
                next.delete(subId);
            else
                next.add(subId);
            return next;
        });
    }, []);
    return (React.createElement("div", { className: "wf-tree", role: "tree", "aria-label": "Workflow pipeline" }, stages.map((stage) => (React.createElement(StageBlock, { key: stage.id, stage: stage, expanded: expandedStages.has(stage.id), onToggle: () => toggleStage(stage.id), selectedStepId: selectedStepId, onSelectStep: onSelectStep, expandedSubtasks: expandedSubtasks, onToggleSubtask: toggleSubtask })))));
}
export { ACCENT };
